import { convertMoneyToMinorUnits, formatMoneyWithoutTrailingZeros } from '@theme/money-formatting';

/**
 * Dual-thumb price range slider for the price filter.
 *
 * Works internally in MINOR units (cents/yen/fils) so it can reuse the theme's
 * currency-aware money utilities instead of `parseFloat`, which breaks on any
 * server-rendered value that carries a thousands separator (e.g. `money_without_currency`
 * renders 1234 as "1,234.00", and `parseFloat("1,234.00") === 1`). Server-provided
 * values (`data-range-max` and the hidden inputs' `value`) are parsed with
 * `convertMoneyToMinorUnits`; drag labels are formatted with `formatMoneyWithoutTrailingZeros`
 * so they match the server-rendered labels (which use `money_without_trailing_zeros`).
 *
 * Committed filter values are whole major units (e.g. "1234"), matching the native
 * price-facet inputs, and the internal step is one major unit.
 *
 * @extends {HTMLElement}
 */
class PriceRangeSliderComponent extends HTMLElement {
  /** @type {HTMLElement | null} */ #minThumb = null;
  /** @type {HTMLElement | null} */ #maxThumb = null;
  /** @type {HTMLElement | null} */ #fill = null;
  /** @type {HTMLElement | null} */ #minLabel = null;
  /** @type {HTMLElement | null} */ #maxLabel = null;
  /** @type {HTMLElement | null} */ #trackWrapper = null;
  /** @type {HTMLInputElement | null} */ #minInput = null;
  /** @type {HTMLInputElement | null} */ #maxInput = null;
  /** @type {AbortController | undefined} */ #abortController;
  /** @type {ReturnType<typeof setTimeout> | undefined} */ #debounceTimer;
  /** @type {string} */ #currency = 'USD';
  /** @type {string} */ #moneyFormat = '{{amount}}';
  /** One major unit expressed in minor units (currency divisor, e.g. 100 for USD, 1 for JPY). @type {number} */
  #step = 1;
  /** @type {number} */ #rangeMax = 1;
  /** @type {number} */ #minVal = 0;
  /** @type {number} */ #maxVal = 1;

  /** Wires thumb interaction, reads initial values from hidden inputs, and renders. */
  connectedCallback() {
    this.#minThumb = this.querySelector('[ref="minThumb"]');
    this.#maxThumb = this.querySelector('[ref="maxThumb"]');
    this.#fill = this.querySelector('[ref="fill"]');
    this.#minLabel = this.querySelector('[ref="minLabel"]');
    this.#maxLabel = this.querySelector('[ref="maxLabel"]');
    this.#trackWrapper = this.querySelector('[ref="trackWrapper"]');
    this.#minInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="minInput"]'));
    this.#maxInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="maxInput"]'));

    this.#currency = this.dataset.currency || 'USD';

    const moneyTemplate = this.querySelector('template[ref="moneyFormat"]');
    this.#moneyFormat = moneyTemplate?.content.textContent?.trim() || '{{amount}}';

    // One major unit in minor units. Doubles as the divisor for converting back to
    // major units on commit and as the keyboard/drag step.
    this.#step = convertMoneyToMinorUnits('1', this.#currency) || 1;
    this.#rangeMax = convertMoneyToMinorUnits(this.dataset.rangeMax ?? '', this.#currency) || this.#step;
    this.#minVal = convertMoneyToMinorUnits(this.#minInput?.value ?? '', this.#currency) ?? 0;
    this.#maxVal = convertMoneyToMinorUnits(this.#maxInput?.value ?? '', this.#currency) ?? this.#rangeMax;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#minThumb?.addEventListener('pointerdown', (e) => this.#onPointerDown(e, 'min'), { signal });
    this.#maxThumb?.addEventListener('pointerdown', (e) => this.#onPointerDown(e, 'max'), { signal });
    this.#minThumb?.addEventListener('keydown', (e) => this.#onKeyDown(e, 'min'), { signal });
    this.#maxThumb?.addEventListener('keydown', (e) => this.#onKeyDown(e, 'max'), { signal });
    this.#trackWrapper?.addEventListener('pointerdown', (e) => this.#onTrackPointerDown(e), { signal });

    this.#render();
  }

  /** Aborts pointer/keyboard listeners and cancels any pending commit debounce. */
  disconnectedCallback() {
    this.#abortController?.abort();
    clearTimeout(this.#debounceTimer);
  }

  /**
   * @param {PointerEvent} e
   * @param {'min' | 'max'} type
   */
  #onPointerDown(e, type) {
    e.preventDefault();
    const thumb = type === 'min' ? this.#minThumb : this.#maxThumb;
    if (!thumb) return;

    thumb.focus();
    thumb.setPointerCapture(e.pointerId);

    // Scoped to this drag (not the component's long-lived signal) so the listeners are
    // guaranteed to be removed on pointerup/pointercancel rather than leaking one pair per drag.
    const dragAbortController = new AbortController();
    const { signal } = dragAbortController;

    const onMove = (/** @type {PointerEvent} */ ev) => {
      const val = this.#clientXToValue(ev.clientX);
      if (type === 'min') {
        this.#minVal = Math.min(Math.max(0, val), this.#maxVal - this.#step);
      } else {
        this.#maxVal = Math.max(Math.min(this.#rangeMax, val), this.#minVal + this.#step);
      }
      this.#render();
    };
    const stopDrag = () => {
      dragAbortController.abort();
      this.#commit();
    };

    thumb.addEventListener('pointermove', onMove, { signal });
    thumb.addEventListener('pointerup', stopDrag, { once: true, signal });
    thumb.addEventListener('pointercancel', stopDrag, { once: true, signal });
  }

  /**
   * Clicking the track (not a thumb) jumps the nearest thumb to that position and commits.
   * Dragging is untouched: this is a no-op when the pointerdown target is a thumb, since
   * `e.target` stays the thumb element through bubbling and the thumb's own listener handles it.
   * @param {PointerEvent} e
   */
  #onTrackPointerDown(e) {
    if (e.target === this.#minThumb || e.target === this.#maxThumb) return;

    const val = this.#clientXToValue(e.clientX);
    const type = Math.abs(val - this.#minVal) <= Math.abs(val - this.#maxVal) ? 'min' : 'max';
    const thumb = type === 'min' ? this.#minThumb : this.#maxThumb;

    if (type === 'min') {
      this.#minVal = Math.min(Math.max(0, val), this.#maxVal - this.#step);
    } else {
      this.#maxVal = Math.max(Math.min(this.#rangeMax, val), this.#minVal + this.#step);
    }

    thumb?.focus();
    this.#render();
    this.#commit();
  }

  /**
   * @param {KeyboardEvent} e
   * @param {'min' | 'max'} type
   */
  #onKeyDown(e, type) {
    const step = this.#step;
    let changed = false;

    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      if (type === 'min') this.#minVal = Math.max(0, this.#minVal - step);
      else this.#maxVal = Math.max(this.#minVal + step, this.#maxVal - step);
      changed = true;
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      if (type === 'min') this.#minVal = Math.min(this.#maxVal - step, this.#minVal + step);
      else this.#maxVal = Math.min(this.#rangeMax, this.#maxVal + step);
      changed = true;
    } else if (e.key === 'Home') {
      if (type === 'min') this.#minVal = 0;
      else this.#maxVal = this.#minVal + step;
      changed = true;
    } else if (e.key === 'End') {
      if (type === 'min') this.#minVal = this.#maxVal - step;
      else this.#maxVal = this.#rangeMax;
      changed = true;
    }

    if (changed) {
      e.preventDefault();
      this.#render();
      this.#scheduleCommit();
    }
  }

  /**
   * Converts a pointer x-coordinate to a value in minor units, snapped to whole major units.
   * @param {number} clientX
   * @returns {number}
   */
  #clientXToValue(clientX) {
    if (!this.#trackWrapper) return 0;
    const rect = this.#trackWrapper.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return Math.round((ratio * this.#rangeMax) / this.#step) * this.#step;
  }

  /** Positions thumbs and fill track based on current internal values. */
  #render() {
    const minPct = (this.#minVal / this.#rangeMax) * 100;
    const maxPct = (this.#maxVal / this.#rangeMax) * 100;
    // aria-valuenow in major units to match aria-valuemin/aria-valuemax (also major units).
    // aria-valuetext carries the currency-formatted label for ATs that prefer it.
    const minMajor = Math.round(this.#minVal / this.#step);
    const maxMajor = Math.round(this.#maxVal / this.#step);
    const rangeMaxMajor = Math.round(this.#rangeMax / this.#step);

    if (this.#minThumb) {
      this.#minThumb.style.left = `${minPct}%`;
      this.#minThumb.setAttribute('aria-valuenow', String(minMajor));
      this.#minThumb.setAttribute('aria-valuetext', this.#formatMoney(this.#minVal));
      // Cross-constrain the exposed range to what the keyboard actually allows: the min thumb can
      // never pass the max thumb (its ceiling is one step below maxVal), so ATs announce the real
      // reachable range rather than the full track.
      this.#minThumb.setAttribute('aria-valuemin', '0');
      this.#minThumb.setAttribute('aria-valuemax', String(Math.max(0, maxMajor - 1)));
    }
    if (this.#maxThumb) {
      this.#maxThumb.style.left = `${maxPct}%`;
      this.#maxThumb.setAttribute('aria-valuenow', String(maxMajor));
      this.#maxThumb.setAttribute('aria-valuetext', this.#formatMoney(this.#maxVal));
      // Max thumb's floor is one step above minVal (it can never pass the min thumb).
      this.#maxThumb.setAttribute('aria-valuemin', String(Math.min(rangeMaxMajor, minMajor + 1)));
      this.#maxThumb.setAttribute('aria-valuemax', String(rangeMaxMajor));
    }
    if (this.#fill) {
      this.#fill.style.left = `${minPct}%`;
      this.#fill.style.width = `${maxPct - minPct}%`;
    }
    if (this.#minLabel) this.#minLabel.textContent = this.#formatMoney(this.#minVal);
    if (this.#maxLabel) this.#maxLabel.textContent = this.#formatMoney(this.#maxVal);
  }

  /**
   * @param {number} minorValue - value in minor units
   * @returns {string}
   */
  #formatMoney(minorValue) {
    return formatMoneyWithoutTrailingZeros(minorValue, this.#moneyFormat, this.#currency);
  }

  /** Debounces commits so rapid keyboard events only trigger one filter update. */
  #scheduleCommit() {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.#commit(), 400);
  }

  /** Writes major-unit min/max to the hidden inputs, dispatches change events, and updates the summary badge. */
  #commit() {
    // Submit whole major units (e.g. "1234"), matching the native price-facet inputs.
    const minMajor = Math.round(this.#minVal / this.#step);
    const maxMajor = Math.round(this.#maxVal / this.#step);

    if (this.#minInput) {
      this.#minInput.value = this.#minVal > 0 ? String(minMajor) : '';
      this.#minInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.#maxInput) {
      this.#maxInput.value = this.#maxVal < this.#rangeMax ? String(maxMajor) : '';
      this.#maxInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#updateSummary();

    const facetsForm = this.closest('facets-form-component');
    if (facetsForm && typeof /** @type {any} */ (facetsForm).updateFilters === 'function') {
      /** @type {any} */ (facetsForm).updateFilters();
    }
  }

  /** Pushes the current min/max values to a sibling facet-status-component, if present. */
  #updateSummary() {
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');
    if (statusComponent && typeof /** @type {any} */ (statusComponent).updatePriceSummary === 'function') {
      /** @type {any} */ (statusComponent).updatePriceSummary(this.#minInput, this.#maxInput);
    }
  }

  /**
   * Re-reads the hidden inputs and re-renders, without dispatching a commit. Called by
   * dy-search-facets to mirror a sibling slider's committed value onto this one (the desktop
   * sidebar and mobile drawer each render their own instance of the same price facet).
   */
  syncFromInputs() {
    this.#minVal = convertMoneyToMinorUnits(this.#minInput?.value ?? '', this.#currency) ?? 0;
    this.#maxVal = convertMoneyToMinorUnits(this.#maxInput?.value ?? '', this.#currency) ?? this.#rangeMax;
    this.#render();
  }

  /**
   * Update the slider's maximum bound. Clamps thumb positions if they exceed the new bound,
   * then re-renders. Called by dy-search-facets when DY returns updated price facet bounds.
   * @param {number} newMaxMajor - New maximum in major units (same unit as data-range-max).
   */
  update(newMaxMajor) {
    // Was the max thumb resting at the old ceiling (an auto/unconstrained max) rather than a
    // value the shopper placed below it? If so, keep it pinned to the ceiling as that ceiling
    // moves — otherwise a rising bound (e.g. switching from a cheaper facet to a pricier one)
    // would freeze the thumb at the old, lower max. A shopper-chosen max is preserved and only
    // clamped down when it now exceeds the new bound.
    const maxWasAtCeiling = this.#maxVal >= this.#rangeMax;
    this.#rangeMax = convertMoneyToMinorUnits(String(newMaxMajor), this.#currency) || this.#step;
    this.#maxVal = maxWasAtCeiling ? this.#rangeMax : Math.min(this.#maxVal, this.#rangeMax);
    this.#minVal = Math.min(this.#minVal, this.#maxVal - this.#step);
    // (The thumbs' aria-value{min,max} are recomputed authoritatively by #render() below.)
    // Keep the hidden inputs in step with any clamp above so #readDom() never re-queries with a
    // now-out-of-range bound (e.g. a shopper-set max above the shrunk ceiling). Mirror #commit()'s
    // at-extreme convention ('' at floor/ceiling); no `change` dispatch — this is a passive bound
    // refresh from a DY response, not a shopper commit.
    if (this.#minInput) this.#minInput.value = this.#minVal > 0 ? String(Math.round(this.#minVal / this.#step)) : '';
    if (this.#maxInput) this.#maxInput.value = this.#maxVal < this.#rangeMax ? String(Math.round(this.#maxVal / this.#step)) : '';
    this.#render();
  }
}

if (!customElements.get('price-range-slider-component')) {
  customElements.define('price-range-slider-component', PriceRangeSliderComponent);
}
